import DOMPurify from 'dompurify';

import { api } from '../../core/network/api';

/** `GET /cms/legal/:slug` — one admin-managed legal document, from the core CMS. */
export interface LegalPage {
  id: string;
  slug: string;
  title: string;
  content: string;
  updatedAt: string;
}

/** `GET /legal/company` — public legal-entity info, admin-managed `company.*` settings. */
export interface CompanyInfo {
  name: string;
  registrationNumber: string;
  address: string;
  email: string;
  jurisdiction: string;
  country: string;
  gameName: string;
}

/** The directory entry for a document — enough to render a shelf row, not the body. */
export type LegalPageMeta = Pick<LegalPage, 'slug' | 'title'>;

export const fetchLegalPage = (slug: string) => api.get<LegalPage>(`/cms/legal/${slug}`);

/** `GET /legal/pages` — every ACTIVE legal document, for the Papers tab's shelf. */
export const fetchLegalPages = () => api.get<LegalPageMeta[]>('/legal/pages');

export const fetchCompanyInfo = () => api.get<CompanyInfo>('/legal/company');

/**
 * The three founding papers pin to the front of the shelf in a fixed order; anything an admin
 * publishes after them follows alphabetically — same ordering rule as the sibling game's Papers
 * tab, so a newly added policy shows up without a code change here.
 */
const PINNED_SLUGS = ['terms-of-service', 'privacy-policy', 'cookie-policy'];

export function sortLegalPages(pages: LegalPageMeta[]): LegalPageMeta[] {
  const rank = (slug: string): number => {
    const i = PINNED_SLUGS.indexOf(slug);
    return i === -1 ? PINNED_SLUGS.length : i;
  };
  return [...pages].sort((a, b) => rank(a.slug) - rank(b.slug) || a.slug.localeCompare(b.slug));
}

/**
 * Fills `{{company.*}}` / `{{game.name}}` / `{{year}}` placeholders in admin-authored legal HTML
 * and sanitizes the result. The HTML is admin input, not ours — same reason `MetaService`
 * escapes admin-supplied meta values before they land in markup (CLAUDE.md's Meta tags note) —
 * so it is never rendered bare, DOMPurify default config included.
 */
export function fillCompanyVars(html: string, company: CompanyInfo | null): string {
  const vars: Record<string, string> = {
    'company.name': company?.name || 'the Company',
    'company.registration': company?.registrationNumber || '—',
    'company.address': company?.address || '—',
    'company.email': company?.email || 'support@blackline.gg',
    'company.jurisdiction': company?.jurisdiction || company?.country || '—',
    'company.country': company?.country || '—',
    'game.name': company?.gameName || 'Black Line',
    year: String(new Date().getFullYear()),
  };
  const filled = html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key: string) => vars[key] ?? match);
  return DOMPurify.sanitize(filled);
}
